import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Play, CheckCircle, Clock, LogOut, Sun, Moon, Building2, AlertCircle } from 'lucide-react';
import api from '../api/axios';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const CandidateDashboard = () => {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [orgFilter, setOrgFilter] = useState('All');
  
  const [candidateDetails, setCandidateDetails] = useState({ name: 'Candidate', email: '' });

  // Theme Logic
  // --- BULLETPROOF THEME STATE ---
  const [isDark, setIsDark] = useState(() => {
    // 1. Check local storage first
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) return storedTheme === 'dark';
    
    // 2. Fallback to current document class or OS preference
    return document.documentElement.classList.contains('dark') || 
           window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  useEffect(() => {
    const fetchCandidateMe = async () => {
      try {
        const res = await api.get('/candidate/me');
        setCandidateDetails(res.data);
      } catch (err) {
        console.error("Failed to fetch candidate details", err);
        navigate('/candidate-login');
      }
    };

    const fetchInterviews = async () => {
      try {
        const res = await api.get('/candidate/interviews'); 
        
        // 🔥 FRONTEND MATH FALLBACK: Auto-expire pending interviews older than 24h
        const normalizedData = res.data.map(interview => {
          let backendStatus = interview.status ? String(interview.status).toLowerCase().trim() : 'pending';
          
          if (interview.date && backendStatus === 'pending') {
              const timeString = interview.date.endsWith('Z') ? interview.date : `${interview.date}Z`;
              const createdAtDate = new Date(timeString);
              const now = new Date();
              const diffInHours = Math.abs(now - createdAtDate) / (1000 * 60 * 60);
              
              if (diffInHours >= 24) {
                  backendStatus = 'expired';
              }
          }
          return { ...interview, status: backendStatus };
        });

        setInterviews(normalizedData);
      } catch (err) {
        console.error("Failed to fetch interviews", err);
      }
    };
    
    fetchCandidateMe();
    fetchInterviews();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await api.post('/candidate/logout');
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      localStorage.removeItem('candidate_email');
      localStorage.removeItem('candidate_name');
      navigate('/candidate-login');
    }
  };

  const organizations = ['All', ...new Set(interviews.map(i => i.organization))];

  // --- SMART SORTING AND FILTERING ---
  const sortedAndFilteredInterviews = useMemo(() => {
    // 1. Filter
    const filtered = interviews.filter(i => {
      const matchesSearch = i.role.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesOrg = orgFilter === 'All' || i.organization === orgFilter;
      return matchesSearch && matchesOrg;
    });

    // 2. Sort (Pending -> Completed/Selected -> Rejected -> Expired)
    return filtered.sort((a, b) => {
      const getRank = (inv) => {
        if (inv.status === 'pending') return 1; // Top priority
        if (inv.status === 'expired') return 4; // Lowest priority
        if (inv.decision === 'rejected') return 3; 
        return 2; // Completed/Selected/Under Review
      };
      return getRank(a) - getRank(b);
    });
  }, [interviews, searchQuery, orgFilter]);

  const getStatusBadge = (status, decision) => {
    if (status === 'expired') return <span className="px-3 py-1.5 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20 rounded-lg text-[12px] font-bold tracking-wide flex items-center gap-1.5"><AlertCircle size={12}/> Expired</span>;
    if (status === 'pending') return <span className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20 rounded-lg text-[12px] font-bold tracking-wide flex items-center gap-1.5"><Clock size={12}/> Action Required</span>;
    if (decision === 'selected') return <span className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 rounded-lg text-[12px] font-bold tracking-wide flex items-center gap-1.5"><CheckCircle size={12}/> Offer Extended</span>;
    if (decision === 'rejected') return <span className="px-3 py-1.5 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20 rounded-lg text-[12px] font-bold tracking-wide">Not Selected</span>;
    return <span className="px-3 py-1.5 bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20 rounded-lg text-[12px] font-bold tracking-wide">Under Review</span>;
  };

  return (
    <div className="flex-1 h-[100dvh] overflow-y-auto bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans transition-colors duration-500 relative flex flex-col overflow-x-hidden custom-scrollbar">
      <style>{customStyles}</style>

      <div className="fixed top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-indigo-200/40 dark:bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none"></div>
      <div className="fixed bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-violet-200/40 dark:bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none" style={{ animationDelay: '2s' }}></div>

      <header className="border-b border-slate-200 dark:border-white/10 flex flex-row items-center justify-between px-6 lg:px-12 z-40 bg-white/80 dark:bg-[#0a0a0a]/80 backdrop-blur-md shrink-0 py-4 h-20 sm:h-24 sticky top-0">
        
        {/* Left: Greeting & Title */}
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center font-bold text-lg shadow-sm border border-indigo-100 dark:border-indigo-500/20 shrink-0">
            {candidateDetails.name ? candidateDetails.name.charAt(0).toUpperCase() : 'C'}
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-wide text-slate-900 dark:text-white leading-none">Overview</h1>
            <p className="text-[12px] sm:text-[12px] text-slate-500 dark:text-slate-400 tracking-wide font-bold mt-1">
              Hello, {candidateDetails.name.split(' ')[0]}
            </p>
          </div>
        </div>

        {/* Right: Always-Visible Controls */}
        <div className="flex items-center gap-4 sm:gap-6">
          <button 
            onClick={() => setIsDark(!isDark)} 
            className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none ${isDark ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-200 border border-slate-300'}`}
            title="Toggle Theme"
          >
            <span className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full transition-transform duration-300 ${isDark ? 'translate-x-7 bg-indigo-400' : 'translate-x-1 bg-white shadow-sm'}`}>
              {isDark ? <Moon size={10} className="text-[#0a0a0a]" /> : <Sun size={10} className="text-slate-400" />}
            </span>
          </button>

          <div className="border-l border-slate-200 dark:border-white/10 pl-4 sm:pl-6">
            <button onClick={handleLogout} className="w-10 h-10 flex items-center justify-center bg-slate-100 dark:bg-white/5 hover:bg-rose-50 dark:hover:bg-rose-500/10 text-slate-500 hover:text-rose-500 border border-transparent dark:border-white/10 rounded-xl transition-colors" title="Sign Out">
              <LogOut size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="px-6 lg:px-12 py-8 sm:py-10 max-w-[1600px] mx-auto w-full relative z-10 flex-1 flex flex-col">
        
        {/* Metric Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 sm:mb-10">
          <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md border border-slate-200 dark:border-white/10 p-5 sm:p-6 rounded-[1.5rem] flex flex-col shadow-sm dark:shadow-none">
             <span className="text-[12px] sm:text-[12px] font-bold tracking-wide text-slate-500 dark:text-slate-400 mb-2 truncate">Total Assigned</span>
             <span className="text-2xl sm:text-3xl font-light text-slate-900 dark:text-white">{interviews.length}</span>
          </div>
          <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md border border-slate-200 dark:border-white/10 p-5 sm:p-6 rounded-[1.5rem] flex flex-col shadow-sm dark:shadow-none">
             <span className="text-[12px] sm:text-[12px] font-bold tracking-wide text-indigo-500 dark:text-indigo-400 mb-2 truncate">To Do</span>
             <span className="text-2xl sm:text-3xl font-light text-indigo-600 dark:text-indigo-400">{interviews.filter(i => i.status === 'pending').length}</span>
          </div>
          <div className="col-span-2 md:col-span-1 bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md border border-slate-200 dark:border-white/10 p-5 sm:p-6 rounded-[1.5rem] flex flex-col shadow-sm dark:shadow-none">
             <span className="text-[12px] sm:text-[12px] font-bold tracking-wide text-slate-500 dark:text-slate-400 mb-2 truncate">Completed</span>
             <span className="text-2xl sm:text-3xl font-light text-slate-900 dark:text-white">{interviews.filter(i => i.status === 'completed').length}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-5 sm:p-6 rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative w-full group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors" size={16}/>
            <input 
              type="text" placeholder="Search roles..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl text-sm placeholder:text-[12px] text-slate-900 dark:text-white focus:outline-none focus:border-indigo-500 transition-all"
            />
          </div>

          <div className="flex items-center bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-1 w-full md:w-auto shrink-0">
            <Building2 size={14} className="text-slate-400 mr-3 shrink-0"/>
            <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} className="bg-transparent text-[12px] sm:text-xs font-bold tracking-wide text-slate-700 dark:text-slate-300 py-3 sm:py-2 outline-none cursor-pointer w-full truncate">
              {organizations.map(org => <option key={org} value={org} className="bg-white dark:bg-[#111]">{org === 'All' ? 'All Organizations' : org}</option>)}
            </select>
          </div>
        </div>

        {/* Interviews Grid */}
        {sortedAndFilteredInterviews.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-20 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-[2rem] shadow-sm dark:shadow-none">
             <Search size={32} className="text-slate-300 dark:text-slate-600 mb-4" strokeWidth={1}/>
             <h3 className="text-xl font-medium text-slate-900 dark:text-white tracking-wide mb-2">No Interviews Found</h3>
             <p className="text-slate-500 dark:text-slate-400 text-sm font-light px-4">You currently have no interview invitations matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 pb-12">
            {sortedAndFilteredInterviews.map((interview, idx) => (
              <div key={idx} className={`bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-6 sm:p-8 rounded-[2rem] border hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-all duration-300 flex flex-col shadow-sm dark:shadow-none group relative overflow-hidden ${(interview.decision === 'rejected' || interview.status === 'expired') ? 'border-rose-100 dark:border-rose-500/10 opacity-75 grayscale-[30%]' : 'border-slate-200 dark:border-white/10'}`}>
                
                {interview.status === 'pending' && <div className="absolute left-0 top-0 w-1.5 h-full bg-indigo-500"></div>}

                <div className="flex flex-col sm:flex-row justify-between items-start mb-6 gap-4 sm:gap-0">
                  <div>
                    <h3 className="text-xl font-medium dark:font-medium tracking-wide text-slate-900 dark:text-white mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors pr-4">{interview.role}</h3>
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-[12px] font-bold tracking-wide">
                       <Building2 size={14} className="shrink-0"/> <span className="truncate">{interview.organization}</span>
                    </div>
                  </div>
                  <div className="shrink-0 w-full sm:w-auto">
                    {getStatusBadge(interview.status, interview.decision)}
                  </div>
                </div>

                <div className="mt-auto pt-6 border-t border-slate-100 dark:border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                   <div className="flex flex-col gap-1">
                      <span className="text-[12px] font-bold tracking-wide text-slate-400 dark:text-slate-500">Assigned On</span>
                      <span className="text-sm font-medium dark:font-light text-slate-700 dark:text-slate-300">{new Date(interview.date).toLocaleDateString()}</span>
                   </div>
                   
                   {/* ACTION BUTTON RENDERING */}
                   {interview.status === 'pending' ? (
                      <button 
                        onClick={() => navigate(`/interview/${interview.session_id}`)}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-4 sm:py-3.5 rounded-xl text-xs font-bold tracking-wide shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all active:scale-95 flex items-center justify-center gap-2 w-full sm:w-auto"
                      >
                        <Play size={14} fill="currentColor"/> Start Interview
                      </button>
                   ) : interview.status === 'expired' ? (
                      <button disabled className="bg-rose-50/50 dark:bg-rose-500/5 text-rose-400 dark:text-rose-500 border border-rose-200 dark:border-rose-500/10 px-6 py-4 sm:py-3.5 rounded-xl text-xs font-bold tracking-wide cursor-not-allowed w-full sm:w-auto">
                        Link Expired
                      </button>
                   ) : (
                      <button disabled className="bg-slate-100 dark:bg-black/40 text-slate-400 dark:text-slate-600 border border-slate-200 dark:border-white/5 px-6 py-4 sm:py-3.5 rounded-xl text-xs font-bold tracking-wide cursor-not-allowed w-full sm:w-auto">
                        Interview Submitted
                      </button>
                   )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateDashboard;